import { useState, useEffect } from 'react';
import { supabase, Application } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  Search, Filter, ChevronDown, User, Mail, Phone, FileText,
  Clock, Eye, Calendar, CheckCircle, XCircle, Star, MoreVertical,
} from 'lucide-react';
import ApplicantDetail from './ApplicantDetail';

const STATUS_CONFIG = {
  new: { label: 'New', bg: 'bg-blue-100', text: 'text-blue-700', dot: 'bg-blue-500' },
  reviewing: { label: 'Reviewing', bg: 'bg-yellow-100', text: 'text-yellow-700', dot: 'bg-yellow-500' },
  interview: { label: 'Interview', bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
  hired: { label: 'Hired', bg: 'bg-green-100', text: 'text-green-700', dot: 'bg-green-500' },
  rejected: { label: 'Rejected', bg: 'bg-red-100', text: 'text-red-600', dot: 'bg-red-500' },
} as const;

const ALL_STATUSES = ['all', 'new', 'reviewing', 'interview', 'hired', 'rejected'] as const;

export default function ApplicantsList() {
  const { agency } = useAuth();
  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  useEffect(() => {
    if (agency) loadApplications();
  }, [agency]);

  async function loadApplications() {
    setLoading(true);
    const { data } = await supabase
      .from('applications')
      .select('*')
      .eq('agency_id', agency!.id)
      .order('submitted_at', { ascending: false });
    setApplications(data ?? []);
    setLoading(false);
  }

  async function updateStatus(id: string, status: Application['status']) {
    await supabase.from('applications').update({ status }).eq('id', id);
    setApplications((apps) => apps.map((a) => a.id === id ? { ...a, status } : a));
    setMenuOpen(null);
  }

  const filtered = applications.filter((a) => {
    const matchSearch =
      a.candidate_name.toLowerCase().includes(search.toLowerCase()) ||
      a.candidate_email.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || a.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const counts = applications.reduce((acc, a) => {
    acc[a.status] = (acc[a.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  if (selectedId) {
    return (
      <ApplicantDetail
        applicationId={selectedId}
        onBack={() => setSelectedId(null)}
        onStatusChange={(status) => {
          setApplications((apps) => apps.map((a) => a.id === selectedId ? { ...a, status } : a));
        }}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Applicants</h2>
          <p className="text-sm text-gray-500 mt-0.5">{applications.length} total applications</p>
        </div>
      </div>

      {/* Status filter pills */}
      <div className="flex gap-2 mb-5 flex-wrap">
        {ALL_STATUSES.map((s) => {
          const cfg = s !== 'all' ? STATUS_CONFIG[s] : null;
          const count = s === 'all' ? applications.length : (counts[s] || 0);
          return (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === s
                  ? 'bg-blue-600 text-white shadow-sm'
                  : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              {cfg && <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />}
              {s === 'all' ? 'All' : STATUS_CONFIG[s as keyof typeof STATUS_CONFIG].label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${statusFilter === s ? 'bg-blue-500' : 'bg-gray-100 text-gray-500'}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative mb-5">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full pl-10 pr-4 py-2.5 rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <User className="w-8 h-8 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No applicants yet</p>
          <p className="text-gray-400 text-sm mt-1">Share your application link to start receiving candidates.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((app) => {
            const cfg = STATUS_CONFIG[app.status];
            return (
              <div
                key={app.id}
                className="flex items-center gap-4 p-4 bg-white border border-gray-200 hover:border-blue-200 hover:shadow-sm rounded-lg transition-colors cursor-pointer group"
                onClick={() => setSelectedId(app.id)}
              >
                <div className="w-10 h-10 rounded-md bg-blue-50 flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 font-bold text-sm">
                    {app.candidate_name.charAt(0).toUpperCase()}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">{app.candidate_name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-xs text-gray-500 truncate">{app.candidate_email}</span>
                    {app.candidate_phone && (
                      <span className="text-xs text-gray-400">{app.candidate_phone}</span>
                    )}
                  </div>
                </div>

                <div className="hidden sm:flex items-center gap-3 flex-shrink-0">
                  {app.cv_url && (
                    <span className="flex items-center gap-1 text-xs text-blue-600">
                      <FileText className="w-3 h-3" />
                      CV
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    {new Date(app.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                  </span>
                </div>

                <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${cfg.bg} ${cfg.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                  {cfg.label}
                </span>

                {/* Status change menu */}
                <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setMenuOpen(menuOpen === app.id ? null : app.id)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  {menuOpen === app.id && (
                    <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-gray-100 rounded-md shadow-xl z-10 overflow-hidden">
                      {Object.entries(STATUS_CONFIG).map(([key, val]) => (
                        <button
                          key={key}
                          onClick={() => updateStatus(app.id, key as Application['status'])}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-gray-50 transition-colors ${app.status === key ? 'font-semibold' : ''}`}
                        >
                          <span className={`w-2 h-2 rounded-full ${val.dot}`} />
                          {val.label}
                          {app.status === key && <CheckCircle className="w-3 h-3 ml-auto text-green-500" />}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
